// ProfileScreen.jsx — Modern SaaS Version

import React, { useState } from "react";
import {
  FiSave,
  FiLogOut,
  FiUser,
  FiLock,
  FiSettings,
} from "react-icons/fi";

import { useAuth } from "../authContext/AuthProvider";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import "../styles/Profile.css";

const ProfileScreen = () => {
  const { user, logout, updateProfile, updatePassword } = useAuth();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  /* Handle Inputs */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  /* Save Profile */
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await updateProfile({ name: formData.name });

      if (result.success) {
        toast.success("Profile updated successfully");
      } else {
        const error = result.error;
        if (typeof error === "object") {
          Object.entries(error).forEach(([field, msg]) => {
            toast.error(`${field}: ${Array.isArray(msg) ? msg[0] : msg}`);
          });
        } else {
          toast.error(error || "Failed to update profile");
        }
      }
    } catch (err) {
      toast.error("Error updating profile");
    } finally {
      setIsLoading(false);
    }
  };

  /* Password Change */
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    try {
      const result = await updatePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );

      if (result.success) {
        toast.success("Password updated successfully");
        setFormData((p) => ({
          ...p,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
      } else {
        const error = result.error;
        if (typeof error === "object") {
          Object.entries(error).forEach(([field, msg]) => {
            toast.error(`${field}: ${Array.isArray(msg) ? msg[0] : msg}`);
          });
        } else {
          toast.error(error || "Error updating password");
        }
      }
    } catch (err) {
      toast.error("Error updating password");
    } finally {
      setIsLoading(false);
    }
  };

  /* Logout */
  const handleLogout = async () => {
    setIsLogoutLoading(true);
    try {
      await logout();
      toast.success("Logged out successfully");
    } catch (err) {
      toast.error("Logout failed");
    } finally {
      setIsLogoutLoading(false);
    }
  };

  return (
<div className="page-container profile-page">
      {/* HEADER */}
      <div className="profile-header-sa">
        <div className="header-left">
          <div className="header-icon-bubble">
            <FiSettings />
          </div>

          <div>
            <h1 className="profile-title">Account Settings</h1>
            <p className="profile-subtitle">
              Manage your profile & security preferences
            </p>
          </div>
        </div>
      </div>

      {/* GRID */}
      <div className="profile-grid-sa">
        {/* PERSONAL INFO */}
        <div className="profile-card-sa">
          <div className="card-header-sa">
            <div className="card-icon-bubble blue">
              <FiUser />
            </div>
            <div>
              <h2 className="card-title-sa">Personal Information</h2>
              <p className="card-subtitle-sa">Update your basic details</p>
            </div>
          </div>

          <form className="profile-form-sa" onSubmit={handleSaveProfile}>
            <div className="input-group-sa">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                className="input-sa"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="input-group-sa">
              <label>Email Address</label>
              <input
                type="email"
                className="input-sa disabled"
                value={formData.email}
                readOnly
              />
              <small className="text-muted">Email cannot be changed</small>
            </div>

            <button
              type="submit"
              className={`sa-btn primary ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              <FiSave />
              Save Changes
            </button>
          </form>
        </div>

        {/* SECURITY */}
        <div className="profile-card-sa">
          <div className="card-header-sa">
            <div className="card-icon-bubble purple">
              <FiLock />
            </div>
            <div>
              <h2 className="card-title-sa">Security Settings</h2>
              <p className="card-subtitle-sa">
                Update your password & security settings
              </p>
            </div>
          </div>

          <form className="profile-form-sa" onSubmit={handleChangePassword}>
            <div className="input-group-sa">
              <label>Current Password</label>
              <input
                type="password"
                name="currentPassword"
                className="input-sa"
                value={formData.currentPassword}
                onChange={handleInputChange}
                placeholder="Enter current password"
                required
              />
            </div>

            <div className="input-group-sa">
              <label>New Password</label>
              <input
                type="password"
                name="newPassword"
                className="input-sa"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                required
              />
            </div>

            <div className="input-group-sa">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                className="input-sa"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                required
              />
            </div>

            <button
              type="submit"
              className={`sa-btn primary ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              <FiSave />
              Update Password
            </button>

            <button
              type="button"
              className="sa-btn danger"
              onClick={() => setShowLogoutModal(true)}
            >
              <FiLogOut />
              Logout
            </button>
          </form>
        </div>
      </div>

      {/* LOGOUT MODAL */}
      <Modal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        centered
        className="logout-modal-sa"
      >
        <Modal.Header>
          <Modal.Title className="text-danger">Confirm Logout</Modal.Title>
          <button
            className="close-modal-sa"
            onClick={() => setShowLogoutModal(false)}
          >
            ×
          </button>
        </Modal.Header>

        <Modal.Body>
          <div className="logout-body-sa">
            <div className="logout-icon-sa">
              <FiLogOut />
            </div>

            <p className="logout-message-sa">
              Are you sure you want to logout?
            </p>

            <div className="logout-actions-sa">
              <button
                className="sa-btn secondary"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>

              <button
                className="sa-btn danger"
                onClick={handleLogout}
                disabled={isLogoutLoading}
              >
                {isLogoutLoading ? "Logging out…" : "Logout"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProfileScreen;
